import { Stack, Button, Typography, Chip, Menu, MenuItem, ListItemIcon, ListItemText } from '@vapor/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateCoParameter } from 'src/app/pages/accounting/documents/checkout-document/checkout-document-form'
import { BillF, DepositF, ReservationF } from '../../../../types/reservation-detail.types'
import CheckOut from '../../../reserved-resources-tab/check-out/check-out'
import CreateCheckoutDocument from './create-checkout-document/create-checkout-document'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { HiCIcon } from 'src/app/components/icons/icon'
import { faScroll } from '@fortawesome/pro-solid-svg-icons'
import AccountBalanceReportViewer from 'src/app/components/report-viewer/account-balance/account-balance-report-viewer'
import BillCreationModal from 'src/app/pages/booking/bill/creation/bill-creation-modal'
import { useBillCreation } from 'src/app/pages/booking/bill/creation/bill-creation.hook'
import { faEllipsisVertical, faFileLines, faMoneyBillTransfer, faPlus } from '@fortawesome/pro-regular-svg-icons'
import UpdateCreditTypePopover from './update-credit-type-popover/update-credit-type-popover'
import { faPrint } from '@fortawesome/pro-regular-svg-icons'
import useInfoSnackBar from 'src/app/hooks/info-snack-bar/use-info-snack-bar'

export type BillsActionsProps = {
  reservation: ReservationF
  bills: BillF[]
  deposits: DepositF[]
  selectedAssignedProductionIds: string[]
  onCreateDocument: (params: CreateCoParameter) => void
  readOnly?: boolean
  propertyId: string
  allDocumentsAreClosed: boolean
  selectedDepositIds: string[]
}

export default function BillsActions({
  reservation,
  bills,
  deposits,
  selectedAssignedProductionIds,
  onCreateDocument,
  allDocumentsAreClosed,
  readOnly,
  propertyId,
  selectedDepositIds,
}: BillsActionsProps) {
  const { t } = useTranslation()

  const showInfo = useInfoSnackBar()

  const [openAccountBalance, setOpenAccountBalance] = useState<boolean>(false)
  const [openBillCreation, setOpenBillCreation] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const [anchorElCreditTypeUpdate, setAnchorElCreditTypeUpdate] = useState<HTMLDivElement | null>(null)
  const handleOpenCreditTypeUpdate = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorElCreditTypeUpdate(event.currentTarget)
  }
  const handleCloseCreditTypeUpdate = () => {
    setAnchorElCreditTypeUpdate(null)
  }
  const openCreditTypeUpdate = Boolean(anchorElCreditTypeUpdate)

  const openBillCreationDialog = () => {
    setOpenBillCreation(true)
  }
  const closeBillCreationDialog = () => {
    setOpenBillCreation(false)
  }

  const openBills = useMemo(() => {
    return bills.filter((b) => b.open)
  }, [bills])

  const { createNewBill } = useBillCreation({
    propertyId: propertyId,
    closeCreationDialog: closeBillCreationDialog,
    reservationIds: [reservation.id],
    reservedResourceIds: reservation.reservedResources?.map((rr) => rr.id),
  })

  const selectedDepositAssignedIds: string[] = []

  for (const deposit of deposits) {
    if (!selectedDepositIds.includes(deposit.id)) continue

    for (const depositAssigned of deposit.depositAssigneds ?? []) {
      selectedDepositAssignedIds.push(depositAssigned.id)
    }
  }

  return (
    <>
      <Stack direction="row">
        <CheckOut //
          readOnly={false}
          reservation={reservation}
          bills={bills}
          deposits={deposits}
          checkOutReservedResourceIds={reservation.reservedResources?.map((rr) => rr.id) ?? []}
          onCreateDocument={onCreateDocument}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Stack direction="row" spacing={1} sx={{ marginRight: 'auto', alignItems: 'center' }}>
          <HiCIcon>
            <FontAwesomeIcon icon={faScroll} fontSize="1.25rem" />
          </HiCIcon>
          <Typography variant="titleSmall">{t('pms.booking.reservation.productions_assigned.title')}</Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {reservation.credit && reservation.reference.customerCompany && (
            <Chip
              onClick={(e) => handleOpenCreditTypeUpdate(e)}
              label={reservation.credit}
              variant={reservation.credit !== 'NC' ? 'outlined' : 'filled'}
              size="small"
              icon={
                <HiCIcon>
                  <FontAwesomeIcon icon={faMoneyBillTransfer} />
                </HiCIcon>
              }
            />
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => {
              setAnchorEl(event.currentTarget)
            }}
            disabled={readOnly}
            startIcon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          >
            {t('pms.booking.reservation.bills_action.more_options')}
          </Button>

          <Menu
            open={anchorEl ? true : false}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            onClose={() => {
              setAnchorEl(null)
            }}
          >
            <MenuItem
              disabled={openBills.length <= 0}
              onClick={() => {
                setOpenAccountBalance(true)
              }}
            >
              <ListItemIcon>
                <HiCIcon>
                  <FontAwesomeIcon icon={faPrint} />
                </HiCIcon>
              </ListItemIcon>
              <ListItemText>{t('pms.booking.reservation.bills_action.bank_statement')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={openBillCreationDialog}>
              <ListItemIcon>
                <HiCIcon>
                  <FontAwesomeIcon icon={faPlus} />
                </HiCIcon>
              </ListItemIcon>
              <ListItemText>{t('pms.booking.reservation.bills_action.add_bill')}</ListItemText>
            </MenuItem>
            <MenuItem
              disabled={readOnly}
              onClick={() => {
                if (!reservation.reference.customerCompany && !reservation.reference.agency) {
                  return showInfo(t('pms.booking.reservation.bills_action.zero_receipt_error'))
                }

                onCreateDocument({
                  createReceiptZero: true,
                  reservationId: reservation.id,
                })
              }}
            >
              <ListItemIcon>
                <HiCIcon>
                  <FontAwesomeIcon icon={faFileLines} />
                </HiCIcon>
              </ListItemIcon>
              <ListItemText>{t('pms.booking.reservation.bills_action.zero_receipt')}</ListItemText>
            </MenuItem>
          </Menu>

          <CreateCheckoutDocument //
            {...{ bills, deposits }}
            selectedAssignedProductionIds={selectedAssignedProductionIds}
            onCreateDocument={onCreateDocument}
            disabled={readOnly || allDocumentsAreClosed ? true : false}
          />
        </Stack>
      </Stack>

      {openAccountBalance && (
        <AccountBalanceReportViewer
          bills={openBills.map((b) => {
            const productionAssignedIds = b.productions?.map((p) => p.id)
            if (selectedAssignedProductionIds.length === 0) {
              return { billId: b.id, productionAssignedIds: productionAssignedIds }
            }

            const filteredProductionAssignedIds = productionAssignedIds?.filter((productionAssignedId) => selectedAssignedProductionIds.includes(productionAssignedId)) ?? []

            if (filteredProductionAssignedIds.length > 0) return { billId: b.id, productionAssignedIds: filteredProductionAssignedIds }
          })}
          open={openAccountBalance}
          setClose={() => setOpenAccountBalance(false)}
          propertyId={propertyId}
          selectedDepositAssignedIds={
            selectedDepositIds.length === 0
              ? undefined // print every deposit
              : selectedDepositAssignedIds
          }
        />
      )}
      <BillCreationModal
        open={openBillCreation}
        setOpen={(value) => (value ? openBillCreationDialog() : closeBillCreationDialog())}
        title={t('pms.booking.passerby-bill.creation-dialog.title-creation')}
        confirmAction={async (billData) => {
          createNewBill(billData)
        }}
        cancelAction={() => closeBillCreationDialog()}
        reservation={reservation}
      />

      <UpdateCreditTypePopover
        reservationId={reservation.id}
        initialValue={reservation.credit}
        open={openCreditTypeUpdate}
        handleClose={handleCloseCreditTypeUpdate}
        anchorEl={anchorElCreditTypeUpdate}
      />
    </>
  )
}
